import {
    db,
} from './firebase.config';
import {
    collection,
    query,
    where,
    getDocs,
    doc,
    addDoc,
    getDoc,
    updateDoc,
    onSnapshot,
    orderBy,
    limit,
    increment,
    serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { sanitizeAttachments } from '../utils/sanitizeAttachments';
import { buildMessagePayload } from '../utils/buildMessagePayload';

// Get all active conversations for a user from the top-level `conversations` collection
export const getActiveConversations = async (userId) => {
    // Ensure userId is provided - critical for data isolation
    if (!userId) {
        console.error('[getActiveConversations] userId is required');
        return [];
    }

    try {
        const conversationsRef = collection(db, 'conversations');
        const q = query(conversationsRef, where('userId', '==', userId), orderBy('lastUpdated', 'desc'));
        const querySnapshot = await getDocs(q);

        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
        }));
    } catch (error) {
        console.error('Error fetching conversations:', error);
        throw error;
    }
};

// Get real-time listener for conversations
export const onConversationsChange = (userId, callback) => {
    // Ensure userId is provided - critical for data isolation
    if (!userId) {
        console.error('[onConversationsChange] userId is required');
        callback([]);
        return () => { };
    }

    try {
        const conversationsRef = collection(db, 'conversations');
        const q = query(conversationsRef, where('userId', '==', userId), orderBy('lastUpdated', 'desc'));

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const conversations = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
            }));
            callback(conversations);
        });

        return unsubscribe;
    } catch (error) {
        console.error('Error setting up real-time listener for conversations:', error);
        throw error;
    }
};

// Get messages for a specific conversation
export const getConversationMessages = async (conversationId) => {
    try {
        const messagesRef = collection(db, `conversations/${conversationId}/messages`);
        const q = query(messagesRef, orderBy('timestamp', 'asc'));
        const querySnapshot = await getDocs(q);

        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
        }));
    } catch (error) {
        console.error('Error fetching messages:', error);
        throw error;
    }
};

// Real-time listener for messages
export const onMessagesChange = (conversationId, callback) => {
    try {
        const messagesRef = collection(db, `conversations/${conversationId}/messages`);
        const q = query(messagesRef, orderBy('timestamp', 'asc'));

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const messages = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
            }));
            callback(messages);
        });

        return unsubscribe;
    } catch (error) {
        console.error('Error setting up messages listener:', error);
        throw error;
    }
};

/**
 * Mark a conversation as read
 * Sets unreadCount to 0 and updates lastReadAt timestamp
 * Should be called when user opens/views a conversation
 * 
 * @param {string} conversationId - The conversation ID to mark as read
 * @returns {Promise<void>}
 */
export const markConversationAsRead = async (conversationId) => {
    if (!conversationId) {
        console.warn('[markConversationAsRead] conversationId is required');
        return;
    }

    try {
        const conversationRef = doc(db, 'conversations', conversationId);

        // Get current conversation to check if update is needed
        const conversationSnap = await getDoc(conversationRef);
        if (!conversationSnap.exists()) {
            console.warn('[markConversationAsRead] Conversation not found:', conversationId);
            return;
        }

        const conversationData = conversationSnap.data();

        // Only update if there are unread messages (avoid unnecessary writes)
        if (conversationData.unreadCount > 0 || conversationData.unread === true) {
            await updateDoc(conversationRef, {
                unreadCount: 0,
                unread: false, // Keep for backward compatibility
                lastReadAt: serverTimestamp(),
            });
            console.log('[markConversationAsRead] Marked as read:', conversationId);
        }
    } catch (error) {
        console.error('[markConversationAsRead] Error:', error);
        // Don't throw - marking as read is not critical
    }
};

/**
 * Increment unread count for a conversation
 * Used when a new message arrives and the user is NOT viewing the conversation
 * 
 * @param {string} conversationId - The conversation ID
 * @returns {Promise<void>}
 */
export const incrementUnreadCount = async (conversationId) => {
    if (!conversationId) {
        console.warn('[incrementUnreadCount] conversationId is required');
        return;
    }

    try {
        const conversationRef = doc(db, 'conversations', conversationId);
        await updateDoc(conversationRef, {
            unreadCount: increment(1),
            unread: true, // Keep for backward compatibility
        });
        console.log('[incrementUnreadCount] Incremented unread for:', conversationId);
    } catch (error) {
        console.error('[incrementUnreadCount] Error:', error);
    }
};

/**
 * Sync a conversation's contact info (email, phone, name) with the latest
 * lead data.  Call this whenever a lead's details are updated so that the
 * conversation document stays in sync and future messages use the correct
 * address / phone number.
 *
 * @param {string} leadId   – The lead whose details changed
 * @param {Object} updates  – Only the fields that changed, e.g. { email, phone, firstName, lastName, fullName, name }
 * @returns {Promise<void>}
 */
export const syncConversationContactInfo = async (leadId, updates) => {
    if (!leadId || !updates || Object.keys(updates).length === 0) return;

    try {
        // Find the conversation(s) linked to this lead
        const conversationsRef = collection(db, 'conversations');
        const q = query(conversationsRef, where('leadId', '==', leadId));
        const snap = await getDocs(q);
        if (snap.empty) return;

        // Build the subset of conversation-level fields that need updating
        const convoUpdates = {};
        if (updates.email !== undefined) convoUpdates.email = updates.email;
        if (updates.phone !== undefined) convoUpdates.phone = updates.phone;
        if (updates.fullName !== undefined) convoUpdates.name = updates.fullName;
        else if (updates.name !== undefined) convoUpdates.name = updates.name;
        if (updates.firstName !== undefined) convoUpdates.firstName = updates.firstName;
        if (updates.lastName !== undefined) convoUpdates.lastName = updates.lastName;

        if (Object.keys(convoUpdates).length === 0) return;

        // Update every conversation belonging to this lead (usually just one)
        const promises = snap.docs.map((d) => updateDoc(d.ref, convoUpdates));
        await Promise.all(promises);
        console.log('[conversation.service] Synced contact info to conversation(s) for lead:', leadId, convoUpdates);
    } catch (err) {
        // Non-critical — log but don't throw so the caller's mutation still succeeds
        console.error('[conversation.service] Failed to sync conversation contact info:', err);
    }
};

// Send a message in a conversation
// CRITICAL: For SMS + EMAIL, message is only saved to Firebase AFTER delivery is confirmed
// If delivery fails, no data is written to Firebase - only a toast is shown
export const sendMessage = async (conversationId, message) => {
    try {
        const conversationRef = doc(db, `conversations/${conversationId}`);

        // Get conversation details
        const conversationSnap = await getDoc(conversationRef);
        const conversationData = conversationSnap.data() || {};

        // ── Resolve FRESH contact info from the lead record ──────────
        // The conversation document may hold stale email / phone from
        // when it was first created.  Always prefer the current lead.
        let freshEmail = conversationData.email;
        let freshPhone = conversationData.phone;

        if (conversationData.leadId) {
            try {
                const leadSnap = await getDoc(doc(db, 'leads', conversationData.leadId));
                if (leadSnap.exists()) {
                    const leadData = leadSnap.data();
                    if (leadData.email) freshEmail = leadData.email;
                    if (leadData.phone) freshPhone = leadData.phone;

                    // Opportunistically sync stale conversation fields so future
                    // reads (toasts, UI) also show the correct values.
                    const drift = {};
                    if (leadData.email && leadData.email !== conversationData.email) drift.email = leadData.email;
                    if (leadData.phone && leadData.phone !== conversationData.phone) drift.phone = leadData.phone;
                    if (Object.keys(drift).length > 0) {
                        updateDoc(conversationRef, drift).catch((e) =>
                            console.warn('[sendMessage] Non-critical: failed to sync conversation contact info:', e)
                        );
                    }
                }
            } catch (leadErr) {
                console.warn('[sendMessage] Could not fetch lead for fresh contact info, using conversation data:', leadErr);
            }
        }

        const messagesRef = collection(db, `conversations/${conversationId}/messages`);

        // =============================================================================
        // SMS: SUCCESS-FIRST PATTERN
        // Call Cloud Function FIRST, only write to Firestore on confirmed success
        // Failed SMS never appears in Firestore or UI
        // =============================================================================
        if (message.channel === 'SMS') {
            if (!freshPhone) {
                return {
                    id: null,
                    success: false,
                    toastMessage: { type: 'error', text: '❌ No phone number on file for this contact.' },
                };
            }

            try {
                // 1. Call SMS Cloud Function FIRST (no Firestore write yet)
                const functions = getFunctions();
                const sendSms = httpsCallable(functions, 'sendSms');
                await sendSms({ to: freshPhone, message: message.text });

                // 2. Only write to Firestore AFTER confirmed success
                const smsMessage = buildMessagePayload({
                    text: message.text,
                    channel: 'SMS',
                    isSender: true,
                    status: 'sent',
                    timestamp: new Date().toISOString(),
                });

                const smsDocRef = await addDoc(messagesRef, smsMessage);

                // 3. Update conversation metadata
                await updateDoc(conversationRef, {
                    lastMessage: message.text,
                    lastUpdated: new Date().toISOString(),
                    lastMessageAt: new Date().toISOString(),
                });

                return {
                    id: smsDocRef.id,
                    success: true,
                    toastMessage: { type: 'success', text: `✅ SMS sent successfully to ${freshPhone}` },
                };
            } catch (smsError) {
                // SMS failed - NO Firestore write, just show error
                console.error('SMS delivery error:', smsError);
                return {
                    id: null,
                    success: false,
                    toastMessage: { type: 'error', text: `❌ SMS failed to send: ${smsError.message || 'Unknown error'}` },
                };
            }
        }

        // =============================================================================
        // EMAIL: SUCCESS-FIRST PATTERN
        // Call Cloud Function FIRST, only write to Firestore on confirmed success
        // Failed email never appears in Firestore or UI
        // =============================================================================
        if (message.channel === 'EMAIL') {
            if (!freshEmail) {
                return {
                    id: null,
                    success: false,
                    toastMessage: { type: 'error', text: '❌ No email on file for this contact.' },
                };
            }

            try {
                const emailTimestamp = new Date().toISOString();
                const functions = getFunctions();
                const sendEmail = httpsCallable(functions, 'sendEmail');
                const idempotencyKey = `${conversationId}:${emailTimestamp}`;
                const result = await sendEmail({
                    to: freshEmail,
                    subject: message.subject || 'New message from Kaeros',
                    text: message.text,
                    html: message.html || null,
                    attachments: message.attachments || [],
                    conversationId,
                    inReplyTo: conversationData.lastInboundMessageId || null,
                    references: conversationData.emailReferences || null,
                    idempotencyKey,
                });

                const emailMessage = buildMessagePayload({
                    text: message.text,
                    channel: 'EMAIL',
                    isSender: true,
                    subject: message.subject,
                    attachments: message.attachments,
                    status: 'sent',
                    emailMessageId: result.data?.messageId || null,
                    timestamp: emailTimestamp,
                });

                const emailDocRef = await addDoc(messagesRef, emailMessage);

                await updateDoc(conversationRef, {
                    lastMessage: message.text,
                    lastUpdated: emailTimestamp,
                    lastMessageAt: emailTimestamp,
                });

                return {
                    id: emailDocRef.id,
                    success: true,
                    toastMessage: { type: 'success', text: `✅ Email sent successfully to ${freshEmail}` },
                };
            } catch (emailError) {
                console.error('Email delivery error:', emailError);
                return {
                    id: null,
                    success: false,
                    toastMessage: { type: 'error', text: '❌ Message failed to send. Please try again.' },
                };
            }
        }

        return {
            id: null,
            success: false,
            toastMessage: { type: 'error', text: '❌ Invalid message channel.' },
        };
    } catch (error) {
        console.error('Error sending message:', error);
        // Return error toast instead of throwing
        return {
            id: null,
            success: false,
            toastMessage: {
                type: 'error',
                text: `❌ Message failed to send. Please try again.`,
            },
        };
    }
};

// Create or get a conversation with a lead
export const getOrCreateConversation = async (userId, lead) => {
    // Ensure userId is provided - critical for data isolation
    if (!userId) {
        throw new Error('userId is required to create/get conversation');
    }
    if (!lead?.id) {
        throw new Error('lead with id is required to create/get conversation');
    }

    try {
        const conversationsRef = collection(db, 'conversations');

        // Query for existing conversation with this lead
        const q = query(
            conversationsRef,
            where('userId', '==', userId),
            where('leadId', '==', lead.id)
        );

        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0];
            return {
                id: doc.id,
                ...doc.data(),
            };
        } else {
            // Create new conversation
            // Use fullName if available, otherwise construct from firstName/lastName
            const displayName = lead.fullName || lead.name || '';
            const firstName = lead.firstName || lead.FirstName || displayName.split(' ')[0] || 'Unknown';
            const lastName = lead.lastName || lead.LastName || displayName.split(' ').slice(1).join(' ') || '';
            const fullName = displayName || (lastName ? `${firstName} ${lastName}` : firstName);

            const newConversation = {
                userId: userId,
                leadId: lead.id || '',
                name: fullName,
                firstName: firstName,
                lastName: lastName,
                email: lead.email || '',
                phone: lead.phone || '',
                preview: '',
                lastMessage: '',
                lastUpdated: new Date().toISOString(),
                lastMessageAt: new Date().toISOString(),
                unreadCount: 0,
                lastReadAt: null,
            };

            const docRef = await addDoc(conversationsRef, newConversation);
            return { id: docRef.id, ...newConversation };
        }
    } catch (error) {
        console.error('Error creating/getting conversation:', error);
        throw error;
    }
};

// Search for leads to start a new conversation
export const searchLeads = async (userId, searchQuery) => {
    // Ensure userId is provided - critical for data isolation
    if (!userId) {
        console.error('[searchLeads] userId is required for search');
        return [];
    }

    try {
        const leadsRef = collection(db, 'leads');
        const q = query(
            leadsRef,
            where('userId', '==', userId)
        );

        const querySnapshot = await getDocs(q);
        const allLeads = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
        }));

        // Filter leads by search query
        if (!searchQuery) return allLeads;

        const lowerQuery = searchQuery.toLowerCase();
        return allLeads.map(lead => ({
            ...lead,
            // Use fullName if available, otherwise construct from firstName/lastName or fallback to name
            name: lead.fullName || lead.name || (lead.lastName ? `${lead.firstName || ''} ${lead.lastName}`.trim() : (lead.firstName || 'Unknown')),
        })).filter(lead =>
            lead.name?.toLowerCase().includes(lowerQuery) ||
            lead.email?.toLowerCase().includes(lowerQuery) ||
            lead.phone?.toLowerCase().includes(lowerQuery)
        );
    } catch (error) {
        console.error('Error searching leads:', error);
        throw error;
    }
};

// Get lead details - verifies ownership before returning
export const getLeadDetails = async (userId, leadId) => {
    // Ensure userId is provided - critical for data isolation
    if (!userId) {
        console.error('[getLeadDetails] userId is required');
        return null;
    }
    if (!leadId) {
        console.error('[getLeadDetails] leadId is required');
        return null;
    }

    try {
        const leadRef = doc(db, 'leads', leadId);
        const leadSnap = await getDoc(leadRef);

        if (leadSnap.exists()) {
            const leadData = leadSnap.data();
            // Verify the lead belongs to the requesting user
            if (leadData.userId !== userId) {
                console.warn('[getLeadDetails] Lead does not belong to user:', userId);
                return null;
            }
            return {
                id: leadSnap.id,
                ...leadData,
            };
        }
        return null;
    } catch (error) {
        console.error('Error fetching lead details:', error);
        throw error;
    }
};
